import { FormControl, FormLabel } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { memo } from 'react';
import { Controller } from 'react-hook-form';

import PickersLocalizationProvider from '../PickersLocalizationProvider/PickersLocalizationProvider';

/**
 * ControlledTextField is a wrapper around the Controller component from react-hook-form.
 * It is used to control the input fields in the form.
 * @param {object} props
 * @param {string} props.label - The label for the input field
 * @param {string} props.name - The name of the input field
 * @param {boolean} [props.required] - The required state of the input field
 * @param {boolean} [props.error] - The error state of the input field
 * @param {string} [props.helperText] - The helper text for the input field
 * @param {boolean} [props.fullWidth] - Stretches the form control to the width of its container
 * @param { import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name'>} [props.controllerOptions] - The options for the controller
 * @param {import('@mui/x-date-pickers').DatePickerProps} [props.datePickerProps] - The props for the text field
 * @param {Omit<import('@mui/material/FormLabel').FormLabelProps, 'label' | 'control'>} [props.formLabelProps] - The props for the form control label
 */

const ControlledDatePickerComponent = ({
  name,
  control,
  label = '',
  required = false,
  error = false,
  helperText = '',
  fullWidth = false,
  controllerOptions = {},
  datePickerProps = {},
  formLabelProps = {},
}) => {
  if (!name) {
    throw new Error('ControlledTextField: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledTextField: control is required');
  }

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => (
        <FormControl fullWidth={fullWidth}>
          {label ? (
            <FormLabel component="legend" required={required} {...formLabelProps}>
              {label}
            </FormLabel>
          ) : null}
          <PickersLocalizationProvider>
            <DatePicker
              onChange={(newValue) => field.onChange(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
              value={field.value || null}
              {...datePickerProps}
            />
          </PickersLocalizationProvider>
          {error ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
        </FormControl>
      )}
    />
  );
};


ControlledDatePickerComponent.displayName = 'ControlledDatePicker';

const ControlledDatePicker = memo(ControlledDatePickerComponent);

export default ControlledDatePicker;
